import { useState } from "react";
import { useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { ShoppingCart } from "lucide-react";
import { useOrderRequest } from "@/contexts/OrderRequestContext";
import { showsLeadCta } from "@/lib/leadCtaRoutes";
import RequestQuoteForm from "./RequestQuoteForm";

/**
 * Site-wide floating "Request Quote" button. Shows a live count of items in the
 * request list and opens a modal containing the selected items + contact form.
 */
export default function OrderRequestButton() {
  const { totalQty } = useOrderRequest();
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  // The dedicated /request-quote/ page already renders the full form; no need
  // for the floating button there. Trident has its own cart, so hide there too.
  // /admin/ screens are internal tools, and the button covered their tables.
  if (pathname.startsWith("/request-quote") || pathname.startsWith("/hardscaping/trident") || pathname.startsWith("/admin")) {
    return null;
  }

  // The fleet-program pages have nothing orderable on them, so an empty cart
  // button is just noise there and the assessment CTA leads instead. Once
  // there are items in the list the button comes back regardless of page — a
  // visitor part-way through a request must never land somewhere they cannot
  // finish it.
  if (showsLeadCta(pathname) && totalQty === 0) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          className="h-14 rounded-full shadow-lg pl-5 pr-6"
          size="lg"
          aria-label={`Review order request${totalQty > 0 ? `, ${totalQty} items` : ""}`}
        >
          <ShoppingCart className="h-5 w-5 mr-2" />
          Order Request
          {totalQty > 0 && (
            <span className="ml-2 inline-flex items-center justify-center min-w-[1.5rem] h-6 rounded-full bg-primary-foreground text-primary text-xs font-bold px-1.5">
              {totalQty}
            </span>
          )}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[85dvh] grid-rows-[auto_1fr] gap-0 p-0 overflow-hidden">
        <DialogHeader className="border-b border-border px-5 pt-5 pb-4 pr-12">
          <DialogTitle>Your Order Request</DialogTitle>
          <DialogDescription>
            Review your selected products, then share your company and contact details and our sales team will get back to you with pricing and availability.
          </DialogDescription>
        </DialogHeader>
        <div className="overflow-y-auto overscroll-contain px-5 py-4">
          <RequestQuoteForm onSubmitted={() => setOpen(false)} />
        </div>
      </DialogContent>
    </Dialog>
  );
}
